import React from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Star, Clock, Users, Phone, Car, Flame, Shield } from "lucide-react";

const KalBhairavTemple = () => {
  const templeImage = "/Ujjain/temples/kalbhairav0.jpg";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Kal Bhairav Temple",
    "description": "Ancient temple in Ujjain dedicated to fierce form of Lord Shiva as Kal Bhairav, protector deity and guardian of time",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Kal Bhairav Temple Road",
      "addressLocality": "Ujjain",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 05:00-22:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.6",
      "reviewCount": "3200"
    }
  };

  const highlights = [
    {
      icon: <Flame className="w-6 h-6" />,
      title: "Fierce Deity",
      description: "Powerful form of Lord Shiva as Kal Bhairav, the destroyer of fear and protector of devotees"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Guardian of Ujjain",
      description: "Traditional guardian deity of Ujjain city, protecting residents and pilgrims from evil forces"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Unique Offerings",
      description: "Famous for accepting liquor offerings, making it one of the most unique temples in India"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Ancient Tradition",
      description: "Centuries-old temple with continuous worship tradition and powerful spiritual energy"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free Entry" },
    { label: "Timings", value: "5:00 AM - 10:00 PM (Daily)" },
    { label: "Special Offering", value: "Liquor (Traditional)" },
    { label: "Best Time", value: "Tuesday & Saturday" },
    { label: "Duration", value: "1-2 hours" },
    { label: "Facilities", value: "Prasadam counter, Shoe stand" }
  ];

  const templeFeatures = [
    {
      title: "Main Sanctum",
      description: "Sacred shrine housing the powerful idol of Kal Bhairav with fierce appearance",
      highlight: "Divine protection energy"
    },
    {
      title: "Offering Counter", 
      description: "Special counter for traditional liquor offerings to appease the deity",
      highlight: "Unique temple tradition"
    },
    {
      title: "Prayer Hall",
      description: "Spacious area for devotees to offer prayers and seek blessings",
      highlight: "Powerful spiritual vibrations"
    },
    {
      title: "Temple Courtyard",
      description: "Open area for gatherings and special ceremonies during festivals",
      highlight: "Community worship space"
    },
    {
      title: "Sacred Tree",
      description: "Ancient tree within temple premises considered sacred by devotees",
      highlight: "Natural sanctity"
    },
    {
      title: "Dhuni (Sacred Fire)",
      description: "Perpetual sacred fire maintained by temple priests for rituals",
      highlight: "Continuous divine presence"
    }
  ];

  const nearbyAttractions = [
    { name: "Mahakaleshwar Temple", distance: "2 km", time: "8 mins" },
    { name: "Harsiddhi Temple", distance: "2.5 km", time: "10 mins" },
    { name: "Ram Ghat", distance: "1 km", time: "5 mins" },
    { name: "Gopal Mandir", distance: "1.5 km", time: "7 mins" }
  ];

  const devotionalPractices = [
    {
      practice: "Liquor Offering",
      description: "Traditional offering of alcohol to appease Kal Bhairav",
      significance: "Unique among Hindu temples, shows acceptance of all offerings"
    },
    {
      practice: "Tuesday Worship",
      description: "Special prayers on Tuesdays for protection and blessings",
      significance: "Most auspicious day for Bhairav worship"
    },
    {
      practice: "Midnight Aarti",
      description: "Special late-night prayers during important festivals",
      significance: "Kal Bhairav is believed to be most active at night"
    },
    {
      practice: "Protection Prayers",
      description: "Devotees seek protection from evil forces and enemies",
      significance: "Bhairav is the ultimate protector deity"
    }
  ];

  const mythologyStories = [
    {
      title: "Guardian of Ujjain",
      story: "Kal Bhairav was appointed by Lord Shiva as the eternal guardian of Ujjain city, protecting it from all evil forces and ensuring the welfare of its inhabitants."
    },
    {
      title: "Destroyer of Fear",
      story: "The fierce form of Bhairav destroys all fears and negative energies, providing courage and strength to devotees facing difficult situations."
    },
    {
      title: "Time Controller",
      story: "As Kal Bhairav (destroyer of time), this deity controls time itself, granting longevity and protection from untimely death to sincere devotees."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Kal Bhairav Temple Ujjain - Guardian Deity | Fierce Lord Shiva | Aaoghumen</title>
        <meta name="description" content="Visit Kal Bhairav Temple Ujjain with Aaoghumen - experience powerful darshan of the fierce protector deity, unique liquor offerings, and divine protection. Complete guide with timings and significance." />
        <meta name="keywords" content="kal bhairav temple ujjain, bhairav temple ujjain, kal bhairav darshan, ujjain guardian deity, bhairav temple timings, aaoghumen kal bhairav, fierce shiva temple ujjain, protector deity temple, ujjain spiritual tour" />
        
        <meta property="og:title" content="Kal Bhairav Temple - Guardian Deity of Ujjain | Aaoghumen" />
        <meta property="og:description" content="Seek divine protection at Kal Bhairav Temple, where the fierce form of Lord Shiva guards Ujjain city and blesses devotees with courage and strength." />
        <meta property="og:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/ujjain/kal-bhairav-temple" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kal Bhairav Temple - Fierce Protector | Ujjain | Aaoghumen" />
        <meta name="twitter:description" content="Experience the powerful energy of Kal Bhairav, the guardian deity of Ujjain, and receive divine protection and blessings." />
        <meta name="twitter:image" content={templeImage} />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/ujjain/kal-bhairav-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Kal Bhairav Temple Ujjain guardian deity"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Kal Bhairav Temple</span> - Guardian of Ujjain
              </h1>
              <p className="text-xl mb-6">
                Seek the powerful protection of Kal Bhairav, the fierce manifestation of Lord Shiva who guards Ujjain city and destroys all fears, negativity, and evil forces threatening his devotees.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.6 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>5 AM - 10 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Kal Bhairav Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Kal Bhairav Temple stands as one of Ujjain's most powerful and unique spiritual destinations, dedicated to the fierce form of Lord Shiva known as Kal Bhairav - the destroyer of time and protector against all evil forces. This ancient temple holds a special place in Hindu tradition as the guardian deity of Ujjain city, where Lord Bhairav eternally watches over the residents and pilgrims, ensuring their safety and spiritual well-being. The temple's significance extends beyond typical worship, representing the fierce protective aspect of divine energy.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                What makes this temple truly extraordinary is its acceptance of liquor offerings, a practice unique among Hindu temples that demonstrates the all-accepting nature of divine consciousness. Devotees offer alcohol to Kal Bhairav as a traditional practice, believing it appeases the fierce deity and ensures his protection. The temple atmosphere is charged with powerful spiritual energy, and many devotees report experiencing immediate relief from fears, enemies, and negative influences after sincere worship here.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, delve deep into the mystical traditions and powerful energy of Kal Bhairav Temple. Our experienced guides explain the profound significance of Bhairav worship, the mythology behind the fierce deity, and the proper methods of seeking his blessings. Discover why this temple has been a source of courage and protection for countless devotees throughout the centuries.
              </p>
            </div>
          </section>

          {/* Temple Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-red-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Devotional Practices */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Sacred Devotional Practices</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {devotionalPractices.map((practice, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-red-600 mb-3">{practice.practice}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{practice.description}</p>
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <p className="text-yellow-800 font-medium text-xs">{practice.significance}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Temple Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Architecture & Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templeFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-red-600 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{feature.description}</p>
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <p className="text-orange-800 font-medium text-xs">{feature.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Mythology & Stories */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Sacred Mythology & Stories</h2>
            <div className="grid md:grid-cols-1 gap-6">
              {mythologyStories.map((myth, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-red-600 mb-4">{myth.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{myth.story}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-red-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Spiritual Experience Guide */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Worship & Experience Guide</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-red-600 mb-4">Protection Prayers</h3>
                <p className="text-gray-600 mb-4">Seek divine protection from enemies, evil forces, and negative energies.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Approach with sincere devotion and respect</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Offer traditional liquor if comfortable</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Chant Bhairav mantras for protection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Visit on Tuesdays for maximum benefit</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-red-600 mb-4">Temple Etiquette</h3>
                <p className="text-gray-600 mb-4">Follow proper customs while worshipping the fierce deity.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Remove footwear before entering</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Maintain respectful behavior and speech</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Follow queue system during crowded times</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Photography allowed in outer areas only</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-red-600 mb-4">Best Times to Visit</h3>
                <p className="text-gray-600 mb-4">Optimal times for powerful darshan and divine blessings.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Tuesday mornings (most auspicious)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Saturday evenings (special prayers)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>New moon nights (Amavasya)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Early morning hours (peaceful)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Sacred Sites</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-red-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto/Rickshaw:</strong> From Ujjain station or bus stand, 10-15 mins</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Walking:</strong> 1 km from Mahakaleshwar Temple, pleasant walk</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Local Bus:</strong> City buses connect to nearby main roads</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Street parking available near temple</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Central Ujjain, well-marked on maps</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Kal Bhairav Temple Ujjain"</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Seek Divine Protection at Kal Bhairav Temple</h2>
              <p className="text-xl mb-6">Experience the fierce grace of Lord Bhairav and receive protection from all fears and negativity</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 12345-67890</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Ujjain, Madhya Pradesh</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button className="bg-white text-red-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors">
                  Plan Temple Visit
                </button>
                <button className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-red-600 transition-colors">
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default KalBhairavTemple;